import { api } from './client';

// Re-export types from shared package
export type {
  VisitStatus,
  VisitPriority,
  OwnershipType,
  OwnershipFilter,
  OwnershipFilterValue,
  DeadlineStatus,
  ClientBrief,
  WeeklyVisit,
  ClientPreference,
  PlannedVisitBrief,
  WeeklyVisitItem,
  WeeklyVisitsResponse,
  ActiveVisit,
  Visit,
  VisitHistoryItem,
} from '@shared/types';

import type {
  OwnershipFilter,
  WeeklyVisitsResponse,
  Visit,
  ActiveVisit,
  VisitPriority,
  ClientPreference,
  VisitHistoryItem,
} from '@shared/types';

// ==================== CLIENT INDICATORS ====================

export interface DelegationInfo {
  toEmployeeId: string;
  initials: string; // "ВИ"
  fullName: string; // "Владимир Иванов"
  expiresAt: string; // ISO date
}

export interface ResponsibleEmployee {
  id: string;
  abbr: string | null; // "tm", "vkv" - from email (before @)
  firstName: string;
  lastName: string;
}

export interface ClientIndicators {
  unpaidCount: number; // 0, 1, 2, or 3 (capped)
  delegations: DelegationInfo[];
  visitFrequencyWeeks: number;
  docs: {
    overdue: number; // 21+ days old
    total: number;
  };
  hasInternetProblem: boolean; // At least one server with no internet
  responsibleEmployee: ResponsibleEmployee | null;
}

export interface ClientIndicatorsResponse {
  indicators: Record<number, ClientIndicators>;
  computedAt: string; // ISO timestamp
}

export interface VisitTimePayload {
  startedTime: string;
  endedTime: string;
  timezoneOffsetMinutes?: number;
}

export interface CheckOutPayload {
  note?: string;
  startedTime?: string;
  endedTime?: string;
  timezoneOffsetMinutes?: number;
}

// ==================== API ====================

export const visitsApi = {
  // Weekly visits list
  getWeeklyVisits: async (
    weekCode?: string,
    ownershipFilter?: OwnershipFilter
  ): Promise<WeeklyVisitsResponse> => {
    const params: Record<string, string> = {};
    if (weekCode) params.weekCode = weekCode;
    if (ownershipFilter) {
      // Convert array to comma-separated string
      if (Array.isArray(ownershipFilter)) {
        params.ownershipFilter = ownershipFilter.join(',');
      } else {
        params.ownershipFilter = ownershipFilter;
      }
    }
    const { data } = await api.get('/visits/weekly', { params });
    return data;
  },

  // Departed clients (no invoices in current period, but had in last 12 months)
  getDepartedClients: async (
    ownershipFilter?: OwnershipFilter
  ): Promise<{ visits: WeeklyVisitsResponse['visits'] }> => {
    const params: Record<string, string> = {};
    if (ownershipFilter) {
      if (Array.isArray(ownershipFilter)) {
        params.ownershipFilter = ownershipFilter.join(',');
      } else {
        params.ownershipFilter = ownershipFilter;
      }
    }
    const { data } = await api.get('/visits/departed', { params });
    return data;
  },

  markVisited: async (
    clientId: number,
    note?: string,
    visitedAt?: string
  ): Promise<Visit> => {
    const { data } = await api.post(
      `/visits/weekly/${clientId}/mark-visited`,
      { note, visitedAt }
    );
    return data;
  },

  markSkipped: async (
    clientId: number,
    note?: string
  ): Promise<Visit> => {
    const { data } = await api.post(
      `/visits/weekly/${clientId}/mark-skipped`,
      { note }
    );
    return data;
  },

  undoCheckIn: async (clientId: number): Promise<void> => {
    await api.post(`/visits/weekly/${clientId}/undo-check-in`);
  },

  cancelVisit: async (clientId: number): Promise<Visit> => {
    const { data } = await api.post(`/visits/weekly/${clientId}/cancel`);
    return data;
  },

  planToday: async (clientId: number): Promise<Visit> => {
    const { data } = await api.post(`/visits/weekly/${clientId}/plan-today`);
    return data;
  },

  scheduleVisit: async (
    visitTargetId: number,
    date: string,
    deadline?: string,
    plannedTime?: string,
  ): Promise<Visit> => {
    const { data } = await api.post(`/visits/schedule`, {
      visitTargetId,
      date,
      deadline,
      plannedTime,
    });
    return data;
  },

  setPriority: async (
    clientId: number,
    priority: VisitPriority
  ): Promise<Visit> => {
    const { data } = await api.patch(
      `/visits/weekly/${clientId}/priority`,
      { priority }
    );
    return data;
  },

  // CRUD
  createVisit: async (visit: {
    visitTargetId: number;
    priority?: VisitPriority;
    plannedDate?: string;
    plannedTime?: string;
    plannedDurationMinutes?: number;
    note?: string;
  }): Promise<Visit> => {
    const { data } = await api.post('/visits', visit);
    return data;
  },

  updateVisit: async (
    id: string,
    updates: {
      status?: 'PENDING' | 'ACTIVE' | 'VISITED' | 'SKIPPED' | 'CANCELLED';
      priority?: VisitPriority;
      plannedDate?: string;
      plannedTime?: string;
      plannedDurationMinutes?: number;
      note?: string;
    }
  ): Promise<Visit> => {
    const { data } = await api.patch(`/visits/${id}`, updates);
    return data;
  },

  deleteVisit: async (id: string): Promise<void> => {
    await api.delete(`/visits/${id}`);
  },

  completeVisit: async (id: string): Promise<Visit> => {
    const { data } = await api.post(`/visits/${id}/complete`);
    return data;
  },

  // Check-in / Check-out
  checkIn: async (clientId: number, note?: string): Promise<ActiveVisit> => {
    const { data } = await api.post(`/visits/check-in/${clientId}`, { note });
    return data;
  },

  checkOut: async (payload?: CheckOutPayload): Promise<{ visit: { id: string; clientId: number; durationMinutes: number } }> => {
    const { data } = await api.post('/visits/check-out', payload ?? {});
    return data;
  },

  updateVisitTime: async (id: string, payload: VisitTimePayload): Promise<Visit> => {
    const { data } = await api.patch(`/visits/${id}/time`, payload);
    return data;
  },

  getActiveVisit: async (): Promise<ActiveVisit | null> => {
    const { data } = await api.get('/visits/active');
    return data;
  },

  // Planned visits
  getPlannedVisits: async (
    date?: string,
    dateFrom?: string,
    dateTo?: string
  ): Promise<Visit[]> => {
    const params: Record<string, string> = {};
    if (date) params.date = date;
    if (dateFrom) params.dateFrom = dateFrom;
    if (dateTo) params.dateTo = dateTo;
    const { data } = await api.get('/visits/planned', { params });
    return data;
  },

  // Client visits paginated (past or future)
  getClientVisitsPaginated: async (
    clientId: number,
    mode: 'past' | 'future',
    cursor?: string,
  ): Promise<{ visits: Visit[]; nextCursor: string | null }> => {
    const params: Record<string, string> = { mode };
    if (cursor) params.cursor = cursor;
    const { data } = await api.get(`/visits/client/${clientId}/history-paginated`, { params });
    return data;
  },

  // Client weekly visits (all statuses)
  getClientWeeklyVisits: async (clientId: number): Promise<Visit[]> => {
    const { data } = await api.get(`/visits/client/${clientId}/weekly`);
    return data;
  },

  // Client planned visits (PENDING with plannedDate)
  getClientPlannedVisits: async (clientId: number): Promise<Visit[]> => {
    const { data } = await api.get(`/visits/client/${clientId}/planned`);
    return data;
  },

  // Visit history
  getVisitHistory: async (
    clientId: number,
    limit?: number
  ): Promise<VisitHistoryItem[]> => {
    const params: Record<string, string> = {};
    if (limit) params.limit = String(limit);
    const { data } = await api.get(`/visits/history/${clientId}`, { params });
    return data;
  },

  // Client preferences
  getClientPreference: async (clientId: number, officeId?: string | null): Promise<ClientPreference | null> => {
    const params: Record<string, string> = {};
    if (officeId) params.officeId = officeId;
    const { data } = await api.get(`/visits/clients/${clientId}/preference`, { params });
    return data;
  },

  setClientPreference: async (
    clientId: number,
    preference: {
      officeId?: string | null;
      preferredDayOfWeek?: number | null;
      preferredTime?: string | null;
      note?: string | null;
    }
  ): Promise<ClientPreference> => {
    const { data } = await api.patch(`/visits/clients/${clientId}/preference`, preference);
    return data;
  },

  // Client indicators (payment status, delegations, docs)
  getClientIndicators: async (
    clientIds: number[]
  ): Promise<ClientIndicatorsResponse> => {
    if (clientIds.length === 0) {
      return { indicators: {}, computedAt: new Date().toISOString() };
    }
    const { data } = await api.get('/visits/client-indicators', {
      params: { clientIds: clientIds.join(',') },
    });
    return data;
  },
};
